<template>
  <article class="config">
    <header>
      <div class="header__title">
        <figure class="header__figure"><img src="@/assets/images/icon-config.svg" alt="환경설정 아이콘" /></figure>
        <h1>
          <span class="title__line">
            <span class="title__typography">{{ t('title.config') }}</span>
          </span>
        </h1>
      </div>
      <div>{{ configStore.config.StoreID }} / {{ configStore.config.SubStoreCode }}</div>
    </header>
    <main>
      <ul class="option-container">
        <li>
          <label>POS</label>
          <BaseSelect
            v-model="tempConfig.PosCode"
            :options="posOptions"
            :use-server-value="false"
            :disabled="disabledPos"
          />
        </li>
        <li>
          <label>{{ t('label.mobile_mode') }}</label>
          <BaseSelect v-model="tempConfig.IsMobileMode" type="boolean" :default-value="false" :options="useOptions" />
        </li>
        <li>
          <label>{{ t('select.table_group') }}</label>
          <BaseSelect
            v-model="tempConfig.TableGroupCode"
            :options="tableGroupOptions"
            @update:model-value="handleTableGroupChange"
          />
        </li>
        <li>
          <label>{{ t('label.table') }}</label>
          <BaseSelect v-model="tempConfig.TableCode" :options="tableOptions" :use-server-value="false" />
        </li>
        <li>
          <label>{{ t('label.server_address') }}</label>
          <BaseInput v-model="tempConfig.POS_API_URL" type="text" :placeholder="t('select.default_server')" />
        </li>
        <li>
          <label>{{ t('label.menupan') }}</label>
          <BaseSelect v-model="tempConfig.MenupanCode" :options="menupanOptions" :use-server-value="false" />
        </li>
        <li>
          <label>{{ t('label.menu_position_fixed') }}</label>
          <BaseSelect
            v-model="tempConfig.IsMenuPositionFixed"
            :options="useOptions"
            :server-value="configStore.serverConfig?.IsMenuPositionFixed"
            :disabled="realtimeConfig.Template === 'left-nav-cart'"
          />
        </li>
        <li>
          <label>{{ t('label.order_type') }}</label>
          <BaseSelect
            v-model="tempConfig.IsPaymentModeUsed"
            :options="IsPaymentModeUsedOptions"
            :server-value="configStore.serverConfig?.IsPaymentModeUsed"
          />
        </li>
        <li>
          <label>{{ t('label.template') }}</label>
          <BaseSelect
            v-model="tempConfig.Template"
            :options="templateOptions"
            :server-value="configStore.serverConfig?.Template"
          />
        </li>
        <li>
          <label>{{ t('label.theme') }}</label>
          <BaseSelect
            v-model="tempConfig.Theme"
            :options="themeOptions"
            :server-value="configStore.serverConfig?.Theme"
          />
        </li>
        <li>
          <label>{{ t('label.menu_group_type') }}</label>
          <BaseSelect
            v-model="tempConfig.MenuGroupType"
            :options="menuGroupTypeOptions"
            :server-value="configStore.serverConfig?.MenuGroupType"
            :disabled="isMenuGroupOptionDisabled"
          />
        </li>
        <li>
          <label>{{ t('label.menu_group_count') }}</label>
          <BaseSelect
            v-model="tempConfig.MenuGroupCountPerLine"
            :options="menuGroupCountOptions"
            :server-value="configStore.serverConfig?.MenuGroupCountPerLine"
            :disabled="isMenuGroupOptionDisabled"
          />
        </li>
        <li>
          <label>{{ t('label.menu_count') }}</label>
          <BaseSelect
            v-model="tempConfig.MenuCountPerLine"
            :options="menuCountOptions"
            :server-value="configStore.serverConfig?.MenuCountPerLine"
            :disabled="isMenuCountDisabled"
          />
        </li>
        <li>
          <label>{{ t('label.tablet_rotate_mode') }}</label>
          <BaseSelect
            v-model="tempConfig.RotateMode"
            :options="rotateModeOptions"
            :server-value="configStore.serverConfig?.RotateMode"
          />
        </li>
        <li>
          <label>{{ t('label.widget_mode') }}</label>
          <BaseSelect
            v-model="tempConfig.IsWidgetModeUsed"
            type="boolean"
            :options="useOptions"
            :server-value="configStore.serverConfig?.IsWidgetModeUsed"
          />
        </li>
      </ul>
    </main>
    <footer>
      <div class="footer__button-group">
        <div class="footer__button-item">
          <BaseButton class="outline" @button-click="handleCancelClick">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="25.075" height="25.075" viewBox="0 0 25.075 25.075">
                <path
                  fill="#ffffff"
                  d="M24.258,100.207a2.509,2.509,0,1,0-3.548-3.548l-8.247,8.255L4.207,96.667a2.509,2.509,0,1,0-3.548,3.548l8.255,8.247L.667,116.718a2.509,2.509,0,1,0,3.548,3.548l8.247-8.255,8.255,8.247a2.509,2.509,0,1,0,3.548-3.548l-8.255-8.247Z"
                  transform="translate(0.075 -95.925)"
                />
              </svg>
            </template>
            {{ t('button.cancel') }}
          </BaseButton>
        </div>
        <div class="footer__button-item">
          <BaseButton @button-click="handleConfirmClick">
            <template #icon><img src="@/assets/images/icon-check-white.svg" alt="확인 아이콘" /></template>
            {{ t('button.confirm') }}
          </BaseButton>
        </div>
      </div>
    </footer>
  </article>
</template>

<script setup lang="ts">
  import * as Android from '@/api/android';
  import * as Server from '@/api/server';
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseInput from '@/components/base/BaseInput.vue';
  import BaseSelect from '@/components/base/BaseSelect.vue';
  import { Theme1, Theme2 } from '@/enums/Theme';
  import UserError from '@/error';
  import Config from '@/interfaces/Config';
  import LocalConfig from '@/interfaces/LocalConfig';
  import Menupan from '@/interfaces/Menupan';
  import Pos from '@/interfaces/Pos';
  import RequestAuthLicense from '@/interfaces/RequestAuthLicense';
  import SelectOption from '@/interfaces/SelectOption';
  import Table from '@/interfaces/Table';
  import TableGroup from '@/interfaces/TableGroup';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { wait } from '@/utils';
  import { computed, onMounted, ref, watch, watchEffect } from 'vue';
  import { useI18n } from 'vue-i18n';

  const emits = defineEmits(['close']);

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const configStore = useConfigStore();

  const useOptions: SelectOption[] = [
    { text: t('select.use'), value: true },
    { text: t('select.unuse'), value: false },
  ];
  const templateOptions: SelectOption[] = [
    { text: t('select.left-nav', { number: 1 }), value: 'left-nav' },
    { text: t('select.left-nav-cart', { number: 3 }), value: 'left-nav-cart' },
    { text: t('select.top-nav', { number: 2 }), value: 'top-nav' },
  ];
  const themeOptions1: SelectOption[] = [
    { text: t('select.light'), value: Theme1.Light },
    { text: t('select.dark_gray'), value: Theme1.DarkGray },
    { text: t('select.green'), value: Theme1.Green },
    { text: t('select.blue'), value: Theme1.Blue },
    { text: t('select.light_blue'), value: Theme1.LightBlue },
    { text: t('select.red'), value: Theme1.Red },
  ];
  const themeOptions2: SelectOption[] = [
    { text: t('select.light'), value: Theme2.Light },
    { text: t('select.dark'), value: Theme2.Dark },
    { text: t('select.dark_gray'), value: Theme2.DarkGray },
    { text: t('select.green'), value: Theme2.Green },
    { text: t('select.blue'), value: Theme2.Blue },
    { text: t('select.light_blue'), value: Theme2.LightBlue },
    { text: t('select.dark'), value: Theme2.Dark },
  ];
  const menuGroupTypeOptions: SelectOption[] = [
    { text: t('select.slider'), value: 'slider' },
    { text: t('select.scroll'), value: 'scroll' },
  ];
  const menuGroupCountOptions: SelectOption[] = [
    { text: '2', value: 2 },
    { text: '3', value: 3 },
    { text: '4', value: 4 },
    { text: '5', value: 5 },
    { text: '6', value: 6 },
  ];
  const menuCountOptions: SelectOption[] = [
    { text: '2', value: 2 },
    { text: '3', value: 3 },
    { text: '4', value: 4 },
    { text: '5', value: 5 },
    { text: '6', value: 6 },
  ];
  const IsPaymentModeUsedOptions: SelectOption[] = [
    { text: t('select.use'), value: true, hidden: true },
    { text: t('select.unuse'), value: false },
  ];
  const rotateModeOptions: SelectOption[] = [
    { text: t('select.auto'), value: '0' },
    { text: t('select.landscape_mode'), value: '1' },
    { text: t('select.portrait_mode'), value: '2' },
  ];

  // 로컬 설정 데이터를 복사한 업데이트 되는 임시 설정 데이터
  const tempConfig = ref<LocalConfig>({ ...configStore.localConfig });
  const posList = ref<Pos[]>([]);
  const disabledPos = ref(false);
  const tableGroupList = ref<TableGroup[]>([]);
  const tableList = ref<Table[]>([]);
  const menupanList = ref<Menupan[]>([]);

  // 실시간으로 적용되는 설정 데이터 (임시 설정 데이터 값이 비어있는 경우 서버 설정 값으로 대체)
  const realtimeConfig = computed((): Config => {
    // 빈 문자열이 아닌 값만 필터링
    const filteredTempConfig = Object.fromEntries(Object.entries(tempConfig.value).filter(([_, v]) => v !== ''));
    // 서버 설정 값과 임시 설정 값을 병합하여 반환
    return Object.assign({} as Config, configStore.serverConfig, filteredTempConfig);
  });

  // 메뉴 그룹 타입 및 개수 비활성화 여부
  const isMenuGroupOptionDisabled = computed((): boolean => {
    return realtimeConfig.value.Template === 'left-nav' || realtimeConfig.value.Template === 'left-nav-cart';
  });

  // 메뉴 개수 비활성화 여부
  const isMenuCountDisabled = computed((): boolean => {
    return realtimeConfig.value.Template === 'left-nav-cart' || !!realtimeConfig.value.IsMenuPositionFixed;
  });

  const posOptions = computed((): SelectOption[] => {
    const posOptions = posList.value.map((pos) => {
      const posOption: SelectOption = {
        text: pos.POS_Name,
        value: pos.POS_ID,
      };
      return posOption;
    });

    posOptions.unshift({ text: t('select.select_pos'), value: '' } as SelectOption);

    return posOptions;
  });

  const tableGroupOptions = computed((): SelectOption[] => {
    return tableGroupList.value.map((tableGroup) => {
      const tableGroupOption: SelectOption = {
        text: tableGroup.TableGroupName,
        value: tableGroup.TableGroupCode,
      };
      return tableGroupOption;
    });
  });

  const tableOptions = computed((): SelectOption[] => {
    const tableOptions = tableList.value
      .filter((table) => table.TableGroupCode === tempConfig.value.TableGroupCode)
      .map((table) => {
        const tableOption: SelectOption = {
          text: table.TableName,
          value: table.TableCode,
        };
        return tableOption;
      });

    if (tableOptions.length === 0) {
      tableOptions.unshift({ text: t('select.create_table'), value: '' } as SelectOption);
    }

    return tableOptions;
  });

  const menupanOptions = computed(() => {
    const menupanOptionList = menupanList.value.map((menupan) => {
      return {
        text: menupan.NAME,
        value: menupan.MENUPAN_CODE,
      } as SelectOption;
    });

    menupanOptionList.unshift({
      text: t('select.menu_setup'),
      value: '0000',
    } as SelectOption);

    const serverMenupan = menupanList.value.find((x) => x.MENUPAN_CODE === configStore.serverConfig?.MenupanCode);
    const serverMenupanName = serverMenupan ? serverMenupan.NAME : t('select.menu_setup');
    menupanOptionList.unshift({
      text: `(${t('select.default_server')}) - ${serverMenupanName}`,
      value: '',
    } as SelectOption);

    return menupanOptionList;
  });

  const themeOptions = computed((): SelectOption[] => {
    return realtimeConfig.value.Template === 'top-nav' ? themeOptions2 : themeOptions1;
  });

  // 템플릿이 변경된 경우 변경된 템플릿에 현재 지정된 테마가 존재하지 않으면 설정 값을 삭제하여 테마 Select를 초기화
  watch(isMenuGroupOptionDisabled, (newValue) => {
    if (newValue) {
      if (themeOptions1.every((option) => option.value !== tempConfig.value.Theme)) {
        delete tempConfig.value.Theme;
      }
    } else {
      if (themeOptions2.every((option) => option.value !== tempConfig.value.Theme)) {
        delete tempConfig.value.Theme;
      }
    }
  });

  onMounted(async () => {
    const loadingId = commonStore.showLoading();
    try {
      const configOption = await Server.getConfigOptions(Android.getMacAddress());
      posList.value = configOption.PosList;
      tableGroupList.value = configOption.TableGroupList;
      tableList.value = configOption.TableList;
      menupanList.value = configOption.MenupanList;

      // POS 유효성 체크
      if (posList.value && posList.value.length === 0) {
        throw new UserError(t('error.no_pos'));
      }

      // MAC 주소가 등록된 포스가 있는 경우 자동 선택 및 비활성화
      const usingPos = posList.value.find((pos) => pos.IsUsing);
      if (usingPos) {
        tempConfig.value.PosCode = usingPos.POS_ID;
        disabledPos.value = true;
      }

      // 로컬에 저장된 테이블 그룹 코드가 없다면 첫번째 테이블 그룹 선택
      if (!tempConfig.value.TableGroupCode) {
        tempConfig.value.TableGroupCode = tableGroupOptions.value[0].value?.toString() || null;
      }
    } finally {
      commonStore.hideLoading(loadingId);
    }
  });

  watchEffect(() => {
    // 로컬에 저장된 테이블 코드가 없다면 첫번째 테이블 선택
    // 또는 조회된 테이블 리스트가 비어 있다면 테이블코드에 NULL값 설정
    if (!tempConfig.value.TableCode) {
      tempConfig.value.TableCode = tableOptions.value[0].value?.toString() || null;
    }

    // 선택된 테이블의 이름을 로컬에 설정
    // if (tempConfig.value.TableCode) {
    //   tempConfig.value.TableName =
    //     tableList.value.find(
    //       (table) =>
    //         table.TableGroupCode === tempConfig.value.TableGroupCode && table.TableCode === tempConfig.value.TableCode,
    //     )?.TableName || '';
    // }
  });

  const handleTableGroupChange = () => {
    tempConfig.value.TableCode = null;
  };

  /** 취소 버튼 클릭 */
  const handleCancelClick = () => emits('close');

  /** 확인 버튼 클릭 */
  const handleConfirmClick = async () => {
    const loadingId = commonStore.showLoading();
    try {
      if (!tempConfig.value.PosCode) {
        throw new UserError(t('error.select_pos'));
      }

      if (!tempConfig.value.TableCode) {
        throw new UserError(t('error.select_table'));
      }

      if (!tempConfig.value.POS_API_URL) tempConfig.value.POS_API_URL = null;

      if (configStore.config.PosCode !== tempConfig.value.PosCode) {
        // 라이센스 인증
        const responseAuthLicense = await Server.authLicense({
          StoreID: configStore.config.StoreID,
          SubStoreCode: configStore.config.SubStoreCode,
          PosID: tempConfig.value.PosCode,
          MacAddress: Android.getMacAddress(),
        } as RequestAuthLicense);
        if (responseAuthLicense.Code !== 0 && responseAuthLicense.Code !== 1) {
          throw new UserError(responseAuthLicense.Message);
        }
      }
      // 설정 저장
      configStore.setLocalConfig(tempConfig.value);

      if ((window as any).AndroidBridge) {
        // 웹뷰인 경우
        Android.saveOption();

        await wait(5000);

        Android.reStart();
      } else {
        location.replace('/home');
      }
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .config {
    display: flex;
    flex-direction: column;
    padding: 2.2388vw;
    height: 100vh;

    header {
      display: flex;
      justify-content: space-between;
      padding-bottom: 0.8955vw;
      border-bottom: 0.0746vw solid var(--border-color-secondary);
      .header__title {
        display: flex;
        align-items: center;
        .header__figure {
          width: 2.6866vw;
          margin-right: 0.7463vw;
          line-height: 1;
          img {
            width: 100%;
          }
        }
        .title__typography {
          position: relative;
          z-index: 1;
        }
        .title__line::after {
          background-color: #ffff00;
          opacity: 0.48;
        }
      }
    }
    main {
      padding: 1.4925vw 0.7463vw 0 0;
      overflow-y: auto;

      .option-container {
        display: grid;
        grid-template-columns: repeat(auto-fill, calc(50% - 0.7463vw));
        gap: 1.4925vw;
        padding-bottom: 1.5625vw;

        @media (max-width: vars.$sm) {
          grid-template-columns: 1fr;
        }

        li {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;

          label {
            font-size: 2.0896vw;
            line-height: 100%;
            @media (max-width: vars.$md) {
              font-size: 14px;
            }
          }

          div {
            width: 29.6875vw;

            @media (max-width: vars.$sm) {
              width: 200px;
            }
          }
        }
      }
      .config-options {
        display: flex;
        gap: 0.7463vw;

        .config-options__item {
          flex: 1;
        }
      }
    }
    footer {
      margin-top: auto;
      padding-top: 1.4925vw;
      width: 100%;
      .footer__button-group {
        display: flex;
        justify-content: space-between;
        gap: 0.7463vw;
        height: 100%;

        .footer__button-item {
          flex-grow: 1;
          height: 100%;
        }
      }
    }
  }
</style>
